import React, { useMemo, useState } from 'react';
import { toast } from 'sonner';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '../../ui/card';
import { Button } from '../../ui/button';
import { TooltipProvider } from '../../ui/tooltip';
import { Truck, Plus, RotateCcw, Sparkles, ArrowDown, AlertTriangle, Info } from 'lucide-react';
import J1939FrameCard from './J1939FrameCard';
import DbcPanel from '../canFrameDecoder/DbcPanel';
import useLiveDecode from './useLiveDecode';
import { j1939FrameDecoderToolApi } from '../../../lib/toolsApi';
import {
  CATEGORY_LABELS, DEFAULT_FRAMES, EXAMPLE_FRAMES, MAX_FRAMES, MAX_SPNS, newFrame, newSpn, toPayloadFrame, withIds,
} from '../../../lib/j1939FrameDecoder';

const J1939FrameDecoder = () => {
  const [frames, setFrames] = useState(() => withIds(DEFAULT_FRAMES));
  const [outcome, setOutcome] = useState(null); // { byId, spnById, dbc, validation }

  const payloadKey = useMemo(() => JSON.stringify({ frames: frames.map(toPayloadFrame) }), [frames]);
  const context = frames.map((f) => ({ id: f.id, spns: f.spns.map((s) => s.id) }));

  // Map the index-based response onto frame / SPN ids as they were at request time.
  const onResult = (results, ctx) => {
    const byId = {};
    const spnById = {};
    results.frames.forEach((r, i) => {
      byId[ctx[i].id] = r;
      r.spns.forEach((s, k) => { spnById[ctx[i].spns[k]] = s; });
    });
    const issues = results.validation.issues.map((iss) => ({
      ...iss,
      frameIds: iss.frames.map((i) => ctx[i]?.id).filter(Boolean),
      spnIds: iss.spns.map(([i, k]) => ctx[i]?.spns[k]).filter(Boolean),
    }));
    const next = { byId, spnById, dbc: results.dbc, validation: { ...results.validation, issues } };
    setOutcome(next);
    return next;
  };

  const { loading, requestError, pausedForS, requestNow } = useLiveDecode(j1939FrameDecoderToolApi, payloadKey, context, onResult);

  const updateFrame = (id, patch) => setFrames((prev) => prev.map((f) => (f.id === id ? { ...f, ...patch } : f)));
  const updateSpn = (frameId, spnId, patch) => setFrames((prev) => prev.map((f) => (f.id !== frameId ? f : {
    ...f, spns: f.spns.map((s) => (s.id === spnId ? { ...s, ...patch } : s)),
  })));
  const addSpn = (frameId) => setFrames((prev) => prev.map((f) => (
    f.id !== frameId || f.spns.length >= MAX_SPNS ? f : { ...f, spns: [...f.spns, newSpn(f.spns)] })));
  const removeSpn = (frameId, spnId) => setFrames((prev) => prev.map((f) => (
    f.id !== frameId ? f : { ...f, spns: f.spns.filter((s) => s.id !== spnId) })));
  const removeFrame = (id) => setFrames((prev) => prev.filter((f) => f.id !== id));
  const addFrame = () => {
    if (frames.length >= MAX_FRAMES) return;
    const frame = newFrame(frames);
    setFrames((prev) => [...prev, frame]);
    setTimeout(() => document.getElementById(`j1939-frame-${frame.id}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 50);
  };
  const loadExample = () => {
    setFrames(withIds(EXAMPLE_FRAMES));
    toast.success('Example loaded: EEC1 (engine speed, actual torque) and CCVS (wheel-based vehicle speed).');
  };
  const reset = () => setFrames(withIds(DEFAULT_FRAMES));

  const jumpToFrame = (id) => document.getElementById(`j1939-frame-${id}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' });

  const validateNow = async () => {
    const next = await requestNow();
    if (next?.paused) {
      toast.info(`Too many updates in a short time; validation resumes in ${pausedForS} s.`);
      return;
    }
    document.getElementById('j19-dbc-panel')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    if (!next) return;
    const { passed, errorCount } = next.validation;
    if (passed) toast.success('✓ DBC Validation Passed');
    else toast.error(`✕ DBC Validation Failed — ${errorCount} Issue${errorCount === 1 ? '' : 's'} Found`);
  };

  // Issues carry frame ids; the panel and cards work with the current numbering.
  const indexById = Object.fromEntries(frames.map((f, i) => [f.id, i]));
  const panelValidation = outcome && {
    ...outcome.validation,
    issues: outcome.validation.issues.map((iss) => ({
      ...iss,
      frames: iss.frameIds.filter((id) => id in indexById).map((id) => indexById[id]),
    })),
  };

  return (
    <TooltipProvider>
      {/* flex gap, not space-y: the visual-edits Babel plugin wraps mapped children in dev. */}
      <div className="flex flex-col gap-6" data-testid="j1939-frame-decoder">
        <Card className="technical-card">
          <CardHeader>
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
              <div>
                <CardTitle className="font-chivo uppercase tracking-tight flex items-center gap-2">
                  <Truck className="h-5 w-5 text-autoxium-blue" />
                  J1939 Frame Decoder
                </CardTitle>
                <CardDescription>
                  29-bit ID → Priority / PGN / Source Address → SPN decode with J1939 range flags → bit map → J1939 DBC → validation → download.
                  Everything updates live as you edit.
                </CardDescription>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <Button type="button" variant="outline" size="sm" onClick={loadExample} data-testid="j19-example-btn">
                  <Sparkles className="h-3.5 w-3.5" /> Example
                </Button>
                <Button type="button" variant="outline" size="sm" onClick={reset} data-testid="j19-reset-btn">
                  <RotateCcw className="h-3.5 w-3.5" /> Reset
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent className="pt-0">
            <p className="flex items-start gap-2 text-xs text-muted-foreground" data-testid="j19-scope-note">
              <Info className="h-3.5 w-3.5 shrink-0 mt-0.5" />
              <span>
                Single-frame parameter groups only (0–8 data bytes). Multi-packet messages sent with the J1939 transport
                protocol (TP.CM / TP.DT, BAM or RTS/CTS) must be reassembled elsewhere first.
              </span>
            </p>
          </CardContent>
        </Card>

        {frames.map((frame, i) => (
          <J1939FrameCard
            key={frame.id}
            frame={frame}
            index={i}
            result={outcome?.byId[frame.id]}
            spnResults={outcome?.spnById || {}}
            issues={(panelValidation?.issues || []).filter((iss) => iss.frames.includes(i))}
            onChange={(patch) => updateFrame(frame.id, patch)}
            onSpnChange={(spnId, patch) => updateSpn(frame.id, spnId, patch)}
            onAddSpn={() => addSpn(frame.id)}
            onRemoveSpn={(spnId) => removeSpn(frame.id, spnId)}
            onRemove={() => removeFrame(frame.id)}
            canRemove={frames.length > 1}
          />
        ))}

        <div className="flex flex-col items-center gap-2">
          <Button type="button" variant="outline" onClick={addFrame} disabled={frames.length >= MAX_FRAMES} className="border-dashed border-2 w-full sm:w-auto" data-testid="j19-add-frame">
            <Plus className="h-4 w-4" /> Add Frame
          </Button>
          <p className="text-xs text-muted-foreground text-center">
            {frames.length} of {MAX_FRAMES} frames. Frames with the same CAN ID become one DBC message.
          </p>
          <ArrowDown className="h-5 w-5 text-muted-foreground/60" aria-hidden="true" />
        </div>

        {pausedForS > 0 && (
          <Card className="technical-card"><CardContent className="pt-6">
            <div className="flex items-start gap-2 text-sm text-amber-700 dark:text-amber-400" data-testid="j19-rate-limited">
              <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
              <span>
                Live updates are paused because of too many changes in a short time. Results below may not match
                your latest edits; they refresh automatically in {pausedForS} s.
              </span>
            </div>
          </CardContent></Card>
        )}

        {requestError && (
          <Card className="technical-card"><CardContent className="pt-6">
            <div className="flex items-start gap-2 text-sm text-destructive" data-testid="j19-request-error">
              <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" /> <span>{requestError}</span>
            </div>
          </CardContent></Card>
        )}

        <DbcPanel
          prefix="j19"
          frameLabel={(f) => `Frame ${f + 1}`}
          categoryLabels={CATEGORY_LABELS}
          sourceLabel="J1939 frames"
          dbc={outcome?.dbc}
          validation={panelValidation}
          loading={loading}
          onValidate={validateNow}
          onJumpToFrame={(i) => frames[i] && jumpToFrame(frames[i].id)}
        />
      </div>
    </TooltipProvider>
  );
};

export default J1939FrameDecoder;
